import {
  Card,
  Checkbox,
  CustomDatePicker,
  Fieldset,
  Form,
  Grid,
  Radio,
  Spacer,
  toast,
} from '@components/common'
import { ReportsApi } from '@api'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { DEPREC_PROFILES, ERROR_REQUEST, LOCATION_CURRENT_REPORT_PATH } from '@constants'
import ReportFilters from '../ReportFilters'
import { currentAssetLocationReportFilterFields } from '../ReportFilters/report-filter-fields'
import { reportResponseHandler } from '@utils/reports'
import { LocationCurrentReportParams } from '@api/models'
import { DepProfileSelect, ActionButtons } from '../General'
import { useReport } from '@hooks'
import { useEffect } from 'react'
import { getTodaysDate } from '@utils/basic'
import { useMemorizeReport } from '../General/MemorizeReports/useMemorizeReport'
import MemorizeReports from '../General/MemorizeReports/MemorizeReport'
import { useFormMethods } from '../General/useFormMethods'
import { useLocation } from 'react-router-dom'

const defaultValues: LocationCurrentReportParams = {
  assetCompanyID: 0,
  filter: [],
  profile: DEPREC_PROFILES.CORPORATE,
  asOfDate: getTodaysDate(),
  format: 0,
  includeDisposals: false,
  includeAssetDesc: false,
  includePrimaryID: false,
  includeSecondaryID: false,
  includeCategoryDesc: false,
  includeReference: false,
  fileFormat: 'pdf',
}

async function onSubmitHandler(values: LocationCurrentReportParams) {
  await ReportsApi.getInstance()
    .getLocationCurrentReport(values)
    .then(data => reportResponseHandler(data, values.fileFormat))
    .catch(() => toast.error(ERROR_REQUEST))
}

const LocationCurrentReport = () => {
  const location = useLocation()
  const locationState: any = location.state

  const {
    methods,
    isRetrievingTemplate,
    isEditTemplate,
    selectedTemplateDetails,
  } = useFormMethods(locationState, defaultValues)

  const {
    formState: { isSubmitting, errors },
    control,
    setValue,
    watch,
  } = methods

  const {
    depProfilesMap,
    isLoading,
    isSuccess,
    isError,
    isNoCompanies,
    assetCompanyID,
  } = useReport()

  const {
    handleMemorizeTemplateSubmit,
    isMemorizeTemplateModalOpen,
    closeMemorizeTemplateModal,
    triggerMemorizeTemplateModal,
    handleEditTemplateSubmit,
  } = useMemorizeReport({ methods, currentReportPath: LOCATION_CURRENT_REPORT_PATH })

  const fileFormat = watch('fileFormat')
  const format = watch('format')

  useEffect(() => {
    if (isSuccess) {
      setValue('assetCompanyID', assetCompanyID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, assetCompanyID])

  useEffect(() => {
    if (fileFormat !== 'pdf' && format === 1) {
      setValue('format', 0)
    }
  }, [fileFormat])

  function getCheckBoxProps(field: any) {
    return {
      checked: field.value,
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
    }
  }

  function getDateProps(field: any) {
    return {
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
      select: field.value,
      onBlur: field.onBlur,
      error: errors[field] !== undefined,
      errorMsg: errors[field]?.message,
    }
  }

  function getRadioProps(field: any) {
    return {
      name: field.name,
      select: field.value,
      onChange: e => field.onChange(parseInt(e.target.value)),
      ref: field.ref,
      disabled: isSubmitting,
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div>Error</div>
  }

  if (isNoCompanies) {
    return <div>No companies found</div>
  }

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmitHandler)}>
          <Card title={'Report Parameters'}>
            <Grid>
              <Grid.Item>
                <DepProfileSelect depProfilesMap={depProfilesMap} />
              </Grid.Item>

              <Grid.Item>
                <Controller
                  control={control}
                  name="asOfDate"
                  render={({ field }) => (
                    <CustomDatePicker {...getDateProps(field)} label="As of date" />
                  )}
                />
              </Grid.Item>

              <Grid.Item m={6}>
                <Fieldset legend={'Additional Columns To Include'}>
                  <Grid>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeAssetDesc"
                        render={({ field }) => (
                          <Checkbox
                            label="Include asset description"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includePrimaryID"
                        render={({ field }) => (
                          <Checkbox
                            label="Include primary ID"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeSecondaryID"
                        render={({ field }) => (
                          <Checkbox
                            label="Include secondary ID"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeCategoryDesc"
                        render={({ field }) => (
                          <Checkbox
                            label="Include category description"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeReference"
                        render={({ field }) => (
                          <Checkbox
                            label="Include reference"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item m={6}>
                <Fieldset legend={'Options'}>
                  <Grid>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="format"
                        render={({ field }) => (
                          <Radio {...getRadioProps(field)} legend="" labelHidden>
                            <Radio.Option value={0} label="List assets" />
                            {fileFormat === 'pdf' && (
                              <Radio.Option value={1} label="Totals only" />
                            )}
                            <Radio.Option value={2} label="List assets; omit $ values" />
                          </Radio>
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeDisposals"
                        render={({ field }) => (
                          <Checkbox
                            label="Include disposals"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item>
                <ReportFilters
                  filtersTemplateData={
                    isRetrievingTemplate
                      ? locationState.templateDetails.templateData.filter
                      : []
                  }
                  filterFields={currentAssetLocationReportFilterFields}
                  includesTotals={fileFormat === 'pdf'}
                />
              </Grid.Item>
            </Grid>
          </Card>
          <Spacer y="default" />
          <ActionButtons
            isSubmitting={isSubmitting}
            triggerMemorizeTemplateModal={triggerMemorizeTemplateModal}
            isEditTemplate={isEditTemplate}
          />
        </Form>
      </FormProvider>

      <MemorizeReports
        isMemorizeTemplateModalOpen={isMemorizeTemplateModalOpen}
        closeMemorizeTemplateModal={closeMemorizeTemplateModal}
        handleMemorizeTemplateSubmit={handleMemorizeTemplateSubmit}
        handleEditTemplateSubmit={handleEditTemplateSubmit}
        isEditTemplate={isEditTemplate}
        selectedTemplateDetails={selectedTemplateDetails}
      />
    </>
  )
}

export default LocationCurrentReport
